import { LuBadgeCheck, LuCheck, LuGift, LuBook, LuBookOpen, LuBrain, LuCalculator, LuCalendar, LuCircleCheck, LuCrown, LuFlame, LuFlaskConical, LuGlobe, LuGrid3X3, LuLanguages, LuLayers, LuLeaf, LuMedal, LuPlay, LuScroll, LuShield, LuSparkles, LuStar, LuTarget, LuTrophy, LuZap } from "react-icons/lu";
import { useCallback, useState } from "react";
import { ApiError, post } from "../api";
import { play, buzz } from "../sound";
import { useApp } from "../store";
import { Link } from "../router";
import { Ring, coinShower } from "./fx";
import { pop } from "./juice";
import { Coin, Progress } from "./ui";

type Ico = (p: { size?: number; "aria-hidden"?: any }) => any;

/** the server names an icon per group ("spark", "calc" …) – here they become real icons */
export const GROUP_ICONS: Record<string, Ico> = {
  spark: LuSparkles, bolt: LuZap, shield: LuShield, medal: LuMedal, globe: LuGlobe, crown: LuCrown, trophy: LuTrophy,
  book: LuBook, language: LuLanguages, flask: LuFlaskConical, calc: LuCalculator, scroll: LuScroll, leaf: LuLeaf, star: LuStar,
};
/** …and one per sub-goal */
export const DEF_ICONS: Record<string, Ico> = {
  check: LuCircleCheck, play: LuPlay, grid: LuGrid3X3, trophy: LuTrophy, target: LuTarget, fire: LuFlame, book: LuBook, brain: LuBrain,
  calendar: LuCalendar, star: LuStar, crown: LuCrown, badge: LuBadgeCheck, ...GROUP_ICONS,
};
export const KIND_ICONS: Record<string, Ico> = { correct: LuCircleCheck, subject_correct: LuBookOpen, games: LuGrid3X3, rounds: LuPlay, wins: LuTrophy, subjects: LuLayers };

/** every group gets its own colour, so that fifteen badges are easy to tell apart (and feel like a collection) */
export const HUES = [212, 262, 330, 28, 152, 190, 48, 292, 8, 172, 240, 100, 318, 200, 70];

/** the stars of a group: lit at 30 %, 60 % and 100 % of its sub-goals – a little feedback long before the whole group is done */
export const starCount = (done: number, total: number) => [Math.ceil(total * 0.3), Math.ceil(total * 0.6), total].filter((t) => t > 0 && done >= t).length;

/**
 * The few sub-goals that are closest to done – "your next steps". Highest share first, at most three from the same group,
 * and a brand-new player simply gets the first ones of the first group.
 */
export function pickNext(groups: any[], n = 3): { def: any; group: any; hue: number }[] {
  const all = groups.flatMap((g, gi) => g.defs.filter((d: any) => !d.completed).map((d: any) => ({ def: d, group: g, gi, hue: HUES[gi % HUES.length]!, ratio: d.target ? d.progress / d.target : 0 })));
  all.sort((a, b) => b.ratio - a.ratio || a.gi - b.gi || a.def.target - b.def.target);
  const perGroup = new Map<string, number>();
  const out: typeof all = [];
  for (const c of all) {
    const k = perGroup.get(c.group.id) ?? 0;
    if (k >= 3) continue;
    perGroup.set(c.group.id, k + 1);
    out.push(c);
    if (out.length >= n) break;
  }
  return out;
}

/** where the button of a sub-goal leads: a subject goal starts a game with exactly that subject */
export const playLink = (def: any) => (def.subject ? `/spil/quizduel?fag=${def.subject}` : "/spil");

export function NextCard({ def, hue, compact }: { def: any; hue: number; compact?: boolean }) {
  const I = DEF_ICONS[def.icon] ?? LuTarget;
  return (
    <article className={`next-card${compact ? " compact" : ""}`} style={{ ["--h" as any]: hue }}>
      <span className="nc-ico" aria-hidden="true"><I size={compact ? 26 : 32} aria-hidden="true" /></span>
      <div className="nc-main">
        <b title={def.description}>{def.title}</b>
        <Progress value={def.progress} max={def.target} label={`${def.title}: ${def.progress} af ${def.target}`} tone="lime" />
        <small>{def.progress} / {def.target}{def.detail ? ` · ${def.detail}` : ""}</small>
      </div>
      <span className="nc-reward" title="Mønter for delmålet"><Coin /> +{def.rewardCoins}</span>
      {!compact && <Link to={playLink(def)} className="btn small nc-go"><LuPlay aria-hidden="true" size={16} /> Spil</Link>}
    </article>
  );
}

/** one daily or weekly mission: a ring with its picture in the middle, a short title and the reward */
export function MissionRow({ m, onClaim, busy }: { m: any; onClaim?: (el: HTMLElement) => void; busy?: boolean }) {
  const I = KIND_ICONS[m.kind] ?? LuTarget;
  return (
    <div className={`mission${m.completed ? " done" : ""}${m.claimable ? " claimable" : ""}`}>
      <Ring value={m.progress} max={m.target} size={58} stroke={7} tone={m.completed ? "green" : "blue"} label={m.title}>
        {m.completed ? <LuCheck aria-label="Klaret" role="img" size={24} /> : <I size={22} aria-hidden="true" />}
      </Ring>
      <span className="ms-text">
        <b>{m.title}</b>
        <small>{m.claimable ? "Klaret – hent din præmie!" : m.completed ? "Klaret!" : `${Math.min(m.progress, m.target)} / ${m.target}`}</small>
      </span>
      {m.claimable && onClaim ? <ClaimButton coins={m.reward.coins} busy={busy} onClick={onClaim} /> : m.claimed ? <ClaimButton done /> : <span className="ms-reward"><Coin /> {m.reward.coins}</span>}
    </div>
  );
}

/** what the server tells after a collection (`POST /api/goals/claim`) */
export interface Collected { coins: number; xp: number; count: number; titles: string[]; cosmetics: string[]; groups: string[]; defs: string[]; missions: string[] }

/**
 * v21: prizes are collected by hand – a small moment of satisfaction. `claim` pays one thing (or "all"), shows a shower of coins from the button,
 * a "+N mønter" pop, refreshes the player (balance + the little gift bubble in the menu) and then lets the page reload its own lists.
 */
export function useClaim(onDone: () => void) {
  const { reload, toast } = useApp();
  const [busy, setBusy] = useState<string | null>(null);
  const claim = useCallback(async (kind: "def" | "group" | "mission" | "all", id: string | undefined, from: HTMLElement | null) => {
    if (busy) return;
    setBusy(`${kind}:${id ?? ""}`);
    try {
      const r = await post<{ collected: Collected; balance: number }>("/api/goals/claim", { kind, id });
      const c = r.collected;
      if (c.count > 0) {
        coinShower(from, Math.min(34, 10 + Math.round(c.coins / 4)));
        play("coin");
        buzz(30);
        if (c.coins > 0) pop(`+${c.coins} mønter!`, "gold");
        for (const t of c.titles) setTimeout(() => pop(`Ny titel: ${t}`, "good"), 450);
        for (const n of c.cosmetics) setTimeout(() => pop(`Ny ting: ${n}`, "blue"), 900);
      }
      await reload();
      onDone();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Det lykkedes ikke at hente præmien. Prøv igen.", "error");
    } finally {
      setBusy(null);
    }
  }, [busy, reload, onDone, toast]);
  return { claim, busy };
}

/** the glowing "Indløs" button of one finished sub-goal / mission – or the quiet "Hentet" once it has been collected */
export function ClaimButton({ coins, label = "Indløs", busy, onClick, done }: { coins?: number; label?: string; busy?: boolean; done?: boolean; onClick?: (el: HTMLElement) => void }) {
  if (done) return <span className="claimed"><LuCheck aria-hidden="true" size={15} /> Hentet</span>;
  return (
    <button type="button" className="claimbtn" disabled={busy} onClick={(e: any) => onClick?.(e.currentTarget as HTMLElement)}>
      <LuGift aria-hidden="true" size={18} />
      <span>{label}</span>
      {coins ? <b><Coin /> +{coins}</b> : null}
    </button>
  );
}
